import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Hausregeln } from '@/components/ki/Hausregeln'
import { apiErrorText } from '@/lib/api-error'
import { Abschnitt } from '@/lib/speichern'
import { fetchReadingState, setReadingAuto, setReadingConsent } from '../api/incoming-invoices.api'
import { tagText } from '../lib/texte'
import type { ReadingState } from '../types/incoming-invoices.types'

const knopf = 'rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50'
const haupt = 'rounded-md bg-primary px-4 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50'

/** AP-44 7.2: Was immer gilt, ist eine Regel der Stammdaten — die Hausregel ist für das, was sich nicht festlegen lässt. */
const HAUSREGEL_HINWEISE = {
  'incoming_invoices.account': 'Feste Zuordnungen gehören an den Lieferanten, den Artikel oder die Gemeinkosten-Kategorie — dort gelten sie immer, und die KI wird für diese Positionen gar nicht gefragt.',
}

/**
 * AP-34 17.7, Nachtrag 1 5.4 — **KI-Lesung einstellen**: die Einwilligung in die Übermittlung an den
 * KI-Anbieter und ob nach dem Hochladen von selbst gelesen wird. Ohne den Baustein antwortet der
 * Server mit 403 — dann gibt es den Abschnitt nicht.
 */
export function LesungEinstellungen() {
  const [stand, setStand] = useState<ReadingState | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  useEffect(() => {
    let aus = false
    fetchReadingState()
      .then((s) => !aus && setStand(s))
      .catch(() => !aus && setStand(null))
    return () => {
      aus = true
    }
  }, [])
  if (!stand) return null

  const tue = async (was: () => Promise<ReadingState>, text: string) => {
    try {
      setLaeuft(true)
      setFehler(null)
      setStand(await was())
    } catch (e) {
      setFehler(apiErrorText(e, text))
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <>
    <Abschnitt titel="KI-Lesung">
      <div className="space-y-3 text-sm">
        {fehler && <p className="text-destructive">{fehler}</p>}
        <p className="text-muted-foreground">
          Die KI liest Kopf und Positionen einer hochgeladenen Rechnung und schlägt sie vor. Sichere Werte sind vorausgewählt, unsichere gelb markiert; in die Rechnung kommt erst, was jemand übernimmt.
        </p>
        {!stand.tenantAi && (
          <p className="rounded-md bg-amber-500/15 px-3 py-2">
            Die KI ist für deinen Betrieb noch nicht eingeschaltet oder es fehlt ein Schlüssel —{' '}
            <Link className="underline" to="/settings/ai">
              Einstellungen → KI
            </Link>
            .
          </p>
        )}
        <div className="rounded-md border border-border px-3 py-2">
          <p className="font-medium">Einwilligung</p>
          <p className="mt-1 text-muted-foreground">{stand.consentText}</p>
          <div className="mt-2 flex flex-wrap items-center gap-3">
            {stand.consentCurrent ? (
              <>
                <span>Eingewilligt{stand.consentAt ? ` am ${tagText(stand.consentAt)}` : ''}.</span>
                <button type="button" className={knopf} disabled={laeuft} onClick={() => void tue(() => setReadingConsent(false), 'Die Einwilligung ließ sich nicht widerrufen.')}>
                  Widerrufen
                </button>
              </>
            ) : (
              <>
                <span className="text-muted-foreground">Ohne Einwilligung liest die KI nichts.</span>
                <button type="button" className={haupt} disabled={laeuft} onClick={() => void tue(() => setReadingConsent(true), 'Die Einwilligung ließ sich nicht speichern.')}>
                  Einwilligen
                </button>
              </>
            )}
          </div>
        </div>
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={stand.autoRead} disabled={laeuft} onChange={(e) => void tue(() => setReadingAuto({ autoRead: e.target.checked }), 'Die Einstellung ließ sich nicht speichern.')} />
          Nach dem Hochladen von selbst lesen
        </label>
        {/* AP-44 7.3: Die KI schlägt Sachkonten nur vor, wo keine Regel der Stammdaten greift — und nur mit Sachkontenstamm */}
        <div>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={stand.autoAccount} disabled={laeuft} onChange={(e) => void tue(() => setReadingAuto({ autoAccount: e.target.checked }), 'Die Einstellung ließ sich nicht speichern.')} />
            Nach dem Übernehmen einer Lesung Sachkonten vorschlagen
          </label>
          <p className="ml-6 mt-0.5 text-xs text-muted-foreground">
            Für Positionen, denen weder Artikel, Kategorie noch Lieferant ein Sachkonto geben. Der Vorschlag steht neben dem Konto, bis du ihn übernimmst. Braucht einen Sachkontenstamm.
          </p>
        </div>
      </div>
    </Abschnitt>
    {/* AP-44: der Text des Betriebs, der bei jedem Aufruf hinter dem festen Auftrag mitgeht */}
    <Hausregeln pfad="/incoming-invoices/reading/house-rules" hinweise={HAUSREGEL_HINWEISE} />
    </>
  )
}
